import React, { useState } from 'react';
import { toast } from "sonner";
import { scrollToTop } from '../lib/utils';
import { emailService } from '../Api/EmailService';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SecurityQuestion, { useSecurityQuestion } from '../components/SecurityQuestion';
import { hasText, stripLeadingSpaces, trimFormData, requiredError, emailError, errorClass, fieldA11y, requiredName, requiredCompany, requiredText, optionalPhoneError, optionalWebsiteError, validateDescriptionField, escapeFormData } from '../lib/formValidation';
import BotTrap, { useBotTrap } from '../components/BotTrap';
import FieldError from '../components/FieldError';
import { PERSONAL_DOMAINS } from '../data/mockData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, CheckCircle, Handshake, Users, TrendingUp, Award } from 'lucide-react';

// Shared field styling: gray field background, same blue as the card border on focus
const FIELD_CLASS =
  "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 " +
  "data-[placeholder]:text-slate-400 " +
  "focus:border-blue-500 focus:ring-1 focus:ring-blue-500 " +
  "focus-visible:border-blue-500 focus-visible:ring-1 focus-visible:ring-blue-500 " +
  // stop Chrome autofill from painting the field light blue
  "[&:-webkit-autofill]:[-webkit-text-fill-color:#fff] " +
  "[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#1e293b] " +
  "[&:-webkit-autofill:focus]:shadow-[inset_0_0_0_1000px_#1e293b]";

// Dropdown list: same gray as the fields
const DROPDOWN_CLASS =
  "bg-slate-800 border-slate-700 text-white " +
  "[&_[role=option]]:text-white " +
  "[&_[role=option][data-highlighted]]:bg-slate-700 [&_[role=option][data-highlighted]]:text-white " +
  "[&_[role=option]:focus]:bg-slate-700 [&_[role=option]:focus]:text-white";

// Message for each required field (null = valid)
const validators = {
  firstName: (v) => requiredName(v, 'First name', 25),
  lastName: (v) => requiredName(v, 'Last name', 25),
  email: (v) => emailError(v, { workOnly: true, personalDomains: PERSONAL_DOMAINS }),
  company: (v) => requiredCompany(v, 'Company name', 50),
  phone: (v) => optionalPhoneError(v),
  website: (v) => optionalWebsiteError(v),
  partnerType: (v) => requiredError(v, 'Partnership type'),
  services: (v) => validateDescriptionField(v, 2500),
  message: (v) => requiredText(v, 'Reason for partnering', 2500),
};

const PartnerPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    website: '',
    partnerType: '',
    experience: '',
    clientBase: '',
    services: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Show a field's message only after the user has visited it
  const [fieldTouched, setFieldTouched] = useState({});
  // A field shows its message once the user has visited it or typed something,
  // so they always know why the button is disabled
  const getError = (field) => {
    const show = fieldTouched[field] || hasText(formData[field]);
    return show ? validators[field](formData[field]) : null;
  };
  const captcha = useSecurityQuestion();
  const trap = useBotTrap();

  // Button stays disabled until every required field has real text
  // (spaces only do not count) and the security question is answered
  const canSubmit =
    Object.keys(validators).every((field) => !validators[field](formData[field])) &&
    captcha.isCorrect;

  const createEmailPayload = (rawData) => {
    // everything typed by the visitor is escaped before it goes into the HTML email
    const data = escapeFormData(rawData);
    const subject = `New Partner Request from ${rawData.firstName} ${rawData.lastName}${rawData.company ? ` at ${rawData.company}` : ''}`;

    const template = `<!DOCTYPE html PUBLIC "-//W3C//DTD HTML 4.01 Transitional//EN">
<html>
  <head>
    <meta content="text/html;charset=UTF-8" http-equiv="Content-Type">
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,100..900;1,100..900&display=swap" rel="stylesheet">
    <style>
      body {
        font-family: 'Noto Sans', sans-serif;
        color: #000000;
      }
      h2 {
        font-family: 'Noto Sans', sans-serif;
      }
      p {
        font-family: 'Noto Sans', sans-serif;
        margin: 4px 0;
      }
    </style>
  </head>
  <body>
    <div>
      <h2>New Partner Request</h2>
     <p><strong>Name:</strong> ${data.firstName} ${data.lastName}</p>
          <p><strong>Company:</strong> ${data.company}</p>
          <p><strong>Email:</strong> ${data.email}</p>
          <p><strong>Phone:</strong> ${data.phone}</p>
          <p><strong>Website:</strong> ${data.website}</p>
          <p><strong>Partnership Type:</strong> ${data.partnerType}</p>
          <p><strong>Years in Business:</strong> ${data.experience}</p>
          <p><strong>Client Base Size:</strong> ${data.clientBase}</p>
          <p><strong>Services Offered:</strong> ${data.services}</p>
          <p><strong>Message:</strong> ${data.message}</p>
    </div>
  </body>
</html>`;
    return { subject, template };
  };

  const handleSubmit = async (e) => {
      e.preventDefault();
    if (!canSubmit || isSubmitting) return;
    if (trap.looksLikeBot()) {
      // pretend it worked, send nothing
      setIsSubmitted(true);
      return;
    }
      setIsSubmitted(false)
      setIsSubmitting(true)
      const payload = createEmailPayload(trimFormData(formData));
      const promise = emailService.sendEmail(payload); // this returns a promise
  
      // Show toast for this promise
      toast.promise(promise, {
        loading: "Sending partner demo request...",
        success: "Partner request sent successfully!",
        error: "Failed to send partner request. Try again later.",
      });
  
      // Handle the result of the promise
      try {
        await promise;
      captcha.reset();
        setIsSubmitted(true);
        scrollToTop()
      }
      catch (error) {
      captcha.reset();
        setIsSubmitted(false)
        setIsSubmitting(false)
      } finally {
        setIsSubmitting(false);
      }
    };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      // no leading spaces, so a field can never be "only spaces"
      [field]: stripLeadingSpaces(value)
    }));
  };

  // Trim trailing spaces when the user leaves any text field of the form
  const handleFormBlur = (e) => {
    const { id, value, tagName } = e.target;
    if (id in validators) setFieldTouched(prev => ({ ...prev, [id]: true }));
    if ((tagName === 'INPUT' || tagName === 'TEXTAREA') && id in formData && typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed !== value) handleChange(id, trimmed);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen section-padding">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-blue-400/10 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="h-10 w-10 text-blue-400" />
            </div>
            <h1 className="display-md text-white mb-6">Partner Application Received!</h1>
            <p className="body-lg mb-8">
              Thank you for your interest in becoming a CXSquare Solutions partner. Our partnership team
              will review your application and contact you within 3-5 business days.
            </p>
            <div className="bg-slate-800 rounded-lg p-6 mb-8">
              <h3 className="h3 text-white mb-4">What's Next?</h3>
              <div className="space-y-4 text-left">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-400 mt-1" />
                  <div>
                    <p className="body-md font-medium text-white">Application Review</p>
                    <p className="body-sm text-slate-400">Our team will evaluate your application and business fit</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-400 mt-1" />
                  <div>
                    <p className="body-md font-medium text-white">Partnership Discussion</p>
                    <p className="body-sm text-slate-400">We'll schedule a call to discuss partnership opportunities</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-blue-400 mt-1" />
                  <div>
                    <p className="body-md font-medium text-white">Onboarding Process</p>
                    <p className="body-sm text-slate-400">Once approved, we'll begin the partner onboarding process</p>
                  </div>
                </div>
              </div>
            </div>
            <Link to="/">
              <Button className="btn-secondary">
                Back to Homepage
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen section-padding">
      <div className="container">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <Link to="/resources" className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Resources
            </Link>
          </div>

          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-2 mb-6">
              <Handshake className="h-8 w-8 text-blue-400" />
              <h1 className="display-md text-white">Become a CXSquare Partner</h1>
            </div>
            <p className="body-lg max-w-2xl mx-auto">
              Join our growing partner ecosystem and help businesses transform their customer relationships
              with cutting-edge CRM technology.
            </p>
          </div>

          {/* Partner Benefits */}
          <section className="mb-16">
            <h2 className="h2 text-white text-center mb-12">Partnership Benefits</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="feature-card">
                <CardContent className="pt-8 text-center">
                  <div className="w-16 h-16 bg-blue-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <TrendingUp className="h-8 w-8 text-blue-400" />
                  </div>
                  <h3 className="h3 text-white mb-4">Revenue Growth</h3>
                  <p className="body-md text-slate-300">
                    Expand your service offerings and generate recurring revenue through our partner commission structure.
                  </p>
                </CardContent>
              </Card>

              <Card className="feature-card">
                <CardContent className="pt-8 text-center">
                  <div className="w-16 h-16 bg-blue-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Users className="h-8 w-8 text-blue-400" />
                  </div>
                  <h3 className="h3 text-white mb-4">Dedicated Support</h3>
                  <p className="body-md text-slate-300">
                    Get access to dedicated partner support, training resources, and technical assistance.
                  </p>
                </CardContent>
              </Card>

              <Card className="feature-card">
                <CardContent className="pt-8 text-center">
                  <div className="w-16 h-16 bg-blue-400/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Award className="h-8 w-8 text-blue-400" />
                  </div>
                  <h3 className="h3 text-white mb-4">Marketing Resources</h3>
                  <p className="body-md text-slate-300">
                    Access co-marketing opportunities, sales materials, and partner certification programs.
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Partner Application Form */}
            <div className="lg:col-span-2">
              <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h2 text-white">Partner Application</CardTitle>
                  <CardDescription className="body-md">
                    Tell us about your business and how you'd like to partner with CXSquare Solutions.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} onBlur={handleFormBlur} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="text-white">First Name *</Label>
                        <Input
                          id="firstName"
                          autoComplete="given-name"
                          value={formData.firstName}
                          onChange={(e) => handleChange('firstName', e.target.value)}
                          required
                          {...fieldA11y('firstName', getError('firstName'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('firstName'))}`}
                        />
                        <FieldError id="firstName-error" message={getError('firstName')} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="text-white">Last Name *</Label>
                        <Input
                          id="lastName"
                          autoComplete="family-name"
                          value={formData.lastName}
                          onChange={(e) => handleChange('lastName', e.target.value)}
                          required
                          {...fieldA11y('lastName', getError('lastName'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('lastName'))}`}
                        />
                        <FieldError id="lastName-error" message={getError('lastName')} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">Business Email *</Label>
                      <Input
                        id="email"
                        autoComplete="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        {...fieldA11y('email', getError('email'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('email'))}`}
                      />
                        <FieldError id="email-error" message={getError('email')} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-white">Company Name *</Label>
                        <Input
                          id="company"
                          autoComplete="organization"
                          value={formData.company}
                          onChange={(e) => handleChange('company', e.target.value)}
                          required
                          {...fieldA11y('company', getError('company'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('company'))}`}
                        />
                        <FieldError id="company-error" message={getError('company')} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-white">Phone Number</Label>
                        <Input
                          id="phone"
                          autoComplete="tel"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          {...fieldA11y('phone', getError('phone'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('phone'))}`}
                        />
                        <FieldError id="phone-error" message={getError('phone')} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website" className="text-white">Company Website</Label>
                      <Input
                        id="website"
                        autoComplete="url"
                        type="url"
                        value={formData.website}
                        onChange={(e) => handleChange('website', e.target.value)}
                        {...fieldA11y('website', getError('website'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('website'))}`}
                        placeholder="https://yourcompany.com"
                      />
                      <FieldError id="website-error" message={getError('website')} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="partnerType" className="text-white">Partnership Type *</Label>
                      <Select value={formData.partnerType} onValueChange={(value) => handleChange('partnerType', value)} onOpenChange={(open) => { if (!open) setFieldTouched(prev => ({ ...prev, partnerType: true })); }} required>
                        <SelectTrigger id="partnerType" className={`${FIELD_CLASS} ${errorClass(getError('partnerType'))}`}>
                          <SelectValue placeholder="Select partnership type" />
                        </SelectTrigger>
                        <SelectContent className={DROPDOWN_CLASS}>
                          <SelectItem value="reseller">Reseller Partner</SelectItem>
                          <SelectItem value="implementation">Implementation Partner</SelectItem>
                          <SelectItem value="technology">Technology Partner</SelectItem>
                          <SelectItem value="referral">Referral Partner</SelectItem>
                          <SelectItem value="consulting">Consulting Partner</SelectItem>
                        </SelectContent>
                      </Select>
                      <FieldError id="partnerType-error" message={getError('partnerType')} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="experience" className="text-white">Years in Business</Label>
                        <Select value={formData.experience} onValueChange={(value) => handleChange('experience', value)}>
                          <SelectTrigger id="experience" className={FIELD_CLASS}>
                            <SelectValue placeholder="Select experience" />
                          </SelectTrigger>
                          <SelectContent className={DROPDOWN_CLASS}>
                            <SelectItem value="0-2">0-2 years</SelectItem>
                            <SelectItem value="3-5">3-5 years</SelectItem>
                            <SelectItem value="6-10">6-10 years</SelectItem>
                            <SelectItem value="10+">10+ years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clientBase" className="text-white">Client Base Size</Label>
                        <Select value={formData.clientBase} onValueChange={(value) => handleChange('clientBase', value)}>
                          <SelectTrigger id="clientBase" className={FIELD_CLASS}>
                            <SelectValue placeholder="Select client base" />
                          </SelectTrigger>
                          <SelectContent className={DROPDOWN_CLASS}>
                            <SelectItem value="1-10">1-10 clients</SelectItem>
                            <SelectItem value="11-50">11-50 clients</SelectItem>
                            <SelectItem value="51-100">51-100 clients</SelectItem>
                            <SelectItem value="100+">100+ clients</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="services" className="text-white">Current Services Offered</Label>
                      <Textarea
                        id="services"
                        value={formData.services}
                        onChange={(e) => handleChange('services', e.target.value)}
                        placeholder="Describe the services your company currently offers..."
                        {...fieldA11y('services', getError('services'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('services'))} min-h-[100px]`}
                      />
                        <FieldError id="services-error" message={getError('services')} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-white">Why Partner with CXSquare? *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        required
                        placeholder="Tell us why you want to become a CXSquare partner and how you plan to add value..."
                        {...fieldA11y('message', getError('message'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('message'))} min-h-[120px]`}
                      />
                        <FieldError id="message-error" message={getError('message')} />
                    </div>
                    <BotTrap trap={trap} />
                    <SecurityQuestion captcha={captcha} fieldClass={FIELD_CLASS} />
                    <Button type="submit" className="btn-primary w-full glow-effect" disabled={isSubmitting || !canSubmit}>
                      {isSubmitting ? "Sending..." : "Submit Partner Application"}
                    </Button>
                    {/* <Button type="submit" className="btn-primary w-full glow-effect">
                      Submit Partner Application
                    </Button> */}
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Partnership Types */}
            <div className="space-y-6">
              <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h3 text-white">Partnership Types</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="body-md font-medium text-white">Reseller Partner</p>
                    <p className="body-sm text-slate-400">Sell CXSquare solutions directly to your client base</p>
                  </div>
                  <div>
                    <p className="body-md font-medium text-white">Implementation Partner</p>
                    <p className="body-sm text-slate-400">Provide setup, customization, and training services</p>
                  </div>
                  <div>
                    <p className="body-md font-medium text-white">Technology Partner</p>
                    <p className="body-sm text-slate-400">Integrate your solutions with our CRM platform</p>
                  </div>
                  <div>
                    <p className="body-md font-medium text-white">Referral Partner</p>
                    <p className="body-sm text-slate-400">Earn commissions by referring qualified leads</p>
                  </div>
                  <div>
                    <p className="body-md font-medium text-white">Consulting Partner</p>
                    <p className="body-sm text-slate-400">Provide strategic CRM consulting and advisory services</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h3 text-white">Partner Requirements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-blue-400" />
                      <span className="body-sm text-slate-300">Established business with proven track record</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-blue-400" />
                      <span className="body-sm text-slate-300">Experience in CRM or related technology</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-blue-400" />
                      <span className="body-sm text-slate-300">Commitment to customer success</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-blue-400" />
                      <span className="body-sm text-slate-300">Professional sales and support capabilities</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PartnerPage;